import { TFunction } from 'i18next'
import { useNewTranslation } from 'hooks'
import RichInfoTag from 'particles/Tag'
import Text from 'particles/Text'
import styled, { useTheme } from 'particles/themes/styled'
import React from 'react'
import ObjectiveIllustrations from './ObjectiveIllustrations'
import { ObjectiveType } from 'state/campaignTemplates'
import Tooltip from 'particles/Tooltip'

interface ObjectiveDetailsProps {
    type: ObjectiveType
}

interface TagWithTooltip {
    label: string
    tooltipContent?: string
}

const getRelatedActionsMap = (t: TFunction): Record<ObjectiveType, TagWithTooltip[]> => {
    return {
        awareness: [
            {
                label: t('taskType.instagramStory'),
                tooltipContent: t('taskType.instagramStory.awareness.tooltips'),
            },
            {
                label: t('taskType.instagramPost'),
                tooltipContent: t('taskType.instagramPost.awareness.tooltips'),
            },
            {
                label: t('taskType.instagramReels'),
                tooltipContent: t('taskType.instagramReels.awareness.tooltips'),
            },
            {
                label: t('taskType.purchase'),
                tooltipContent: t('taskType.purchase.awareness.tooltips'),
            },
            {
                label: t('taskType.tikTokVideo'),
                tooltipContent: t('taskType.tikTokVideo.awareness.tooltips'),
            },
            {
                label: t('taskType.facebook'),
                tooltipContent: t('taskType.facebook.awareness.tooltips'),
            },
            {
                label: t('taskType.follow'),
                tooltipContent: t('taskType.follow.awareness.tooltips'),
            },
        ],
        traffic: [
            {
                label: t('taskType.instagramStory'),
                tooltipContent: t('taskType.instagramStory.traffic.tooltips'),
            },
            // hide this, because there is no template supported in this objective
            // {
            //     label: t('taskType.instagramPost'),
            //     tooltipContent: t('taskType.instagramPost.traffic.tooltips'),
            // },
            // {
            //     label: t('taskType.instagramReels'),
            //     tooltipContent: t('taskType.instagramReels.traffic.tooltips'),
            // },
            {
                label: t('taskType.purchase'),
                tooltipContent: t('taskType.purchase.awareness.tooltips'),
            },
            // hide this, because there is no template supported in this objective
            // {
            //     label: t('taskType.linkInBio'),
            //     tooltipContent: t('taskType.linkInBio.traffic.tooltips'),
            // },
            {
                label: t('taskType.stickerLink'),
                tooltipContent: t('taskType.stickerLink.traffic.tooltips'),
            },
        ],
        credibility: [
            {
                label: t('taskType.purchase'),
                tooltipContent: t('taskType.purchase.credibility.tooltips'),
            },
            {
                label: t('taskType.follow'),
                tooltipContent: t('taskType.follow.credibility.tooltips'),
            },
            {
                label: t('taskType.reviewTag'),
                tooltipContent: t('taskType.reviewTag.credibility.tooltips'),
            },
        ],
        appPromotion: [
            {
                label: t('taskType.downloadApp'),
                tooltipContent: t('taskType.downloadApp.appPromotion.tooltips'),
            },
            {
                label: t('taskType.registerAnAccount'),
                tooltipContent: t('taskType.registerAnAccount.appPromotion.tooltips'),
            },
        ],
        content: [
            {
                label: t('taskType.imageVideo'),
                tooltipContent: t('taskType.imageVideo.content.tooltips'),
            },
        ],
    }
}

const getKeyMetricsMap = (t: TFunction): Record<ObjectiveType, TagWithTooltip[]> => {
    return {
        awareness: [
            {
                label: t('keyMetricType.potentialCumulativeReach'),
                tooltipContent: t('keyMetricType.potentialCumulativeReach.tooltips'),
            },
            {
                label: t('submission.table.viewsLabel'),
                tooltipContent: t('submission.table.viewsLabel.tooltips'),
            },
            {
                label: t('keyMetricType.engagenent'),
                tooltipContent: t('keyMetricType.engagenent.tooltips'),
            },
        ],
        traffic: [
            {
                label: t('submission.table.linkClicksLabel'),
                tooltipContent: t('submission.table.linkClicksLabel.tooltips'),
            },
            // hide this, because there is no template supported in this objective
            // {
            //     label: t('keyMetricType.promoCode'),
            //     tooltipContent: t('keyMetricType.promoCode.tooltips'),
            // },
        ],
        credibility: [
            {
                label: t('socialMedia.requirement.followers'),
                tooltipContent: t('socialMedia.requirement.followers.tooltips'),
            },
            {
                label: t('keyMetricType.review'),
                tooltipContent: t('keyMetricType.review.tooltips'),
            },
        ],
        appPromotion: [
            {
                label: t('keyMetricType.appDownloads'),
                tooltipContent: t('keyMetricType.appDownloads.tooltips'),
            },
            {
                label: t('keyMetricType.appSignUps'),
                tooltipContent: t('keyMetricType.appSignUps.tooltips'),
            },
        ],
        content: [
            {
                label: t('keyMetricType.imageContent'),
                tooltipContent: t('keyMetricType.imageContent.tooltips'),
            },
            {
                label: t('keyMetricType.videoContent'),
                tooltipContent: t('keyMetricType.videoContent.tooltips'),
            },
        ],
    }
}

const ObjectiveDetails: React.FC<ObjectiveDetailsProps> = ({ type }) => {
    const [t] = useNewTranslation()
    const theme = useTheme()
    const relatedActionsMap = getRelatedActionsMap(t)
    const keyMetricsMap = getKeyMetricsMap(t)
    const colorMap: Record<ObjectiveType, string> = {
        awareness: theme.colors.indicator.ribbonRed,
        traffic: theme.colors.indicator.yolkYellow,
        credibility: theme.colors.indicator.skyBlue,
        appPromotion: theme.colors.indicator.darkGreen,
        content: theme.colors.indicator.darkOrange,
    }

    return (
        <ObjectiveDetailsContainer>
            <div className="illustration-container">
                <ObjectiveIllustrations type={type} />
            </div>

            <div className="tag-container">
                <Text scale="bodyM700">{t('campaign.objectiveModal.taskTag.title')}</Text>
                <div className="tag-list">
                    {relatedActionsMap[type].map((actionText, idx) => (
                        <Tooltip key={actionText.label + idx} label={actionText.tooltipContent}>
                            <RichInfoTag
                                label={actionText.label}
                                showTooltipOverflow={false}
                                customStyle={{
                                    borderColor: colorMap[type],
                                    color: colorMap[type],
                                    backgroundColor: theme.colors.surface.primary,
                                }}
                            />
                        </Tooltip>
                    ))}
                </div>
            </div>
            <div className="tag-container">
                <Text scale="bodyM700">{t('campaign.objectiveModal.keyMetricsTag.title')}</Text>
                <div className="tag-list">
                    {keyMetricsMap[type].map((keyMetric, idx) => (
                        <Tooltip key={keyMetric.label + idx} label={keyMetric.tooltipContent}>
                            <RichInfoTag
                                label={keyMetric.label}
                                showTooltipOverflow={false}
                                customStyle={{
                                    borderColor: theme.colors.indicator.black,
                                    color: theme.colors.indicator.black,
                                    backgroundColor: theme.colors.surface.primary,
                                }}
                            />
                        </Tooltip>
                    ))}
                </div>
            </div>
        </ObjectiveDetailsContainer>
    )
}

export default ObjectiveDetails

const ObjectiveDetailsContainer = styled.div`
    background: ${({ theme }) => theme.colors.surface.primary};
    border: 0.5px solid ${({ theme }) => theme.colors.border.primary};
    min-height: 303px;
    height: fit-content;
    width: 376px;
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[300]};
    box-sizing: border-box;
    flex-shrink: 0;

    .illustration-container {
        height: 123px;
    }

    // handle mobile view diplay
    @media (max-width: 768px) {
        width: 100%;

        .illustration-container {
            height: 113px;
            display: flex;
            justify-content: center;

            img {
                width: auto;
                max-width: 100%;
                object-fit: contain;
                height: 100%;
            }
        }
    }

    .tag-container {
        display: flex;
        flex-direction: column;
        gap: ${({ theme }) => theme.spacing[200]};
        padding: 0px 12px 12px 12px;

        .tag-list {
            display: flex;
            flex-wrap: wrap;
            gap: ${({ theme }) => theme.spacing[200]};
        }
    }
`
